import
{
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Address, CartUpdateDTO } from "@dto";
import { TranslateService } from "@ngx-translate/core";
import { CartService } from "@service/cart/cart.service";
import { CustomerClientService } from "@service/client/customer-client.service";
import { PopupService } from "@service/popup/popup.service";
import { WindowRefService } from "@service/window-service/window-ref.service";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { AddressType } from "../../../../../model/AddressType.model";
import
{
  animateButton,
  showAfterSecond,
  removeAnimateButton
} from "../../../../../utils/utilsFunctions";
import { CustomValidators } from "../../../../../utils/custom.validators";
import { MessageItem } from "app/model/interfaces";

enum ControlNames
{
  EMAIL = "Email",
  FIRSTNAME = "FirstName",
  SECONDNAME = "SecondName",
  PHONE = "Phone",
  ADDRESS = "Street1",
  CITY = "City",
  POSTCODE = "PostCode",
  TAX_ID = "TaxID",
  PERSONAL = "Personal",
  COMPANY = "Company",
  BILLING_COMPANYNAME = "billingCompanyName",
}

enum AccountType
{
  PERSONAL = "personal",
  COMPANY = "company"
}

@Component({
    selector: "app-change-personal-data-popup",
    templateUrl: "./change-personal-data-popup.component.html",
    styleUrls: ["./change-personal-data-popup.component.scss"],
    standalone: false
})
export class ChangePersonalDataPopupComponent implements OnDestroy, OnInit
{
  private destroy$: Subject<void> = new Subject();
  public addressTypeInterface = AddressType;
  public readonly ControlNames = ControlNames;
  public readonly AccountType = AccountType;
  public addressForm: UntypedFormGroup;
  public showForm = false;
  public emailAddressNecessary: boolean;
  public phoneNecessary: boolean;
  public nameNecessary: boolean;
  public addressNecessary: boolean;
  public emailPlaceholder: string = this.translate.instant("cart.addressField.email");
  public firstNamePlaceholder: string = this.translate.instant("cart.addressField.firstName");
  public secondNamePlaceholder: string = this.translate.instant("cart.addressField.lastName");
  public phonePlaceholder: string = this.translate.instant("cart.addressField.phone");
  public addressPlaceholder: string = this.translate.instant("cart.addressField.address");
  public companyNamePlaceholder = this.translate.instant("cart.addressField.companyName");
  public taxIdPlaceholder = this.translate.instant("cart.addressField.taxId");
  public postCodePlaceholder = this.translate.instant("cart.addressField.postCode");
  public cityPlaceholder = this.translate.instant("cart.addressField.city");
  public address: Address;
  public addressType: string;
  private lastAccountType: string;
  private isData: boolean = false;
  public isLoading: boolean;
  public error: MessageItem;
  public loaded = [];

  @Input() private name: string = "";
  @Input() public runFromMenu: boolean = false;
  @Output()
  public readonly refreshDataOnSuccess: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild("nextButton") private nextButton: ElementRef;

  constructor(
    private formBuilder: UntypedFormBuilder,
    public windowRef: WindowRefService,
    private cs: CustomerClientService,
    private changeDet: ChangeDetectorRef,
    private translate: TranslateService,
    private renderer2: Renderer2,
    private cartService: CartService,
    private popupService: PopupService,
  ) { }

  public openModal(
    defaultAddress: Address,
    addressType: AddressType,
    name: boolean,
    address: boolean,
    email: boolean,
    phone: boolean,
    disableReload?: boolean
  ): void
  {
    if (this.windowRef.getNativeWindowInnerWidth() < 1960)
    {
      this.windowRef.blockScroll();
    }

    this.popupService.openPopup(this.name, !!disableReload);

    this.isData = true;
    this.showForm = true;
    this.emailAddressNecessary = email;
    this.phoneNecessary = phone;
    this.addressNecessary = address;
    this.nameNecessary = name;
    this.address = defaultAddress ? defaultAddress : ({} as Address);
    this.addressType = addressType;
    this.lastAccountType = this.address.personal ? this.AccountType.PERSONAL : this.AccountType.COMPANY;
    this.initializeForm();
    this.translateFields();
    this.toggleAccountType(this.lastAccountType);
    showAfterSecond(100, this.loaded, this.changeDet);
    this.changeDet.detectChanges();
  }

  public ngOnDestroy(): void
  {
    this.popupService.clearName(this.name);
    this.destroy$.next();
    this.destroy$.complete();
  }

  public savePersonalData(): void
  {
    this.markAsTouchedAndDirty(this.addressForm.controls);

    if (!this.addressForm.valid)
    {
      this.isLoading = false;
      this.error = {} as MessageItem;
      this.error["message"] = this.translate.instant("error.formIsIncorrect");
      this.error["type"] = "Warning";
      animateButton("error", this.nextButton, this.renderer2, this.changeDet);
      return;

    }

    let dto: CartUpdateDTO = {};

    //if (this.runFromMenu)
    //  {
    //      dto = new CustomerUpdateDTO();
    //}

    if (this.addressType == AddressType.BILLING)
    {
      this.addressType = "billingAdddress";
    }

    dto[this.addressType] = {};
    dto[this.addressType].email = this.addressForm.controls[ControlNames.EMAIL].value;
    dto[this.addressType].city = this.addressForm.controls[ControlNames.CITY].value;
    dto[this.addressType].postCode = this.addressForm.controls[ControlNames.POSTCODE].value;
    dto[this.addressType].street1 = this.addressForm.controls[ControlNames.ADDRESS].value;
    dto[this.addressType].phone = this.addressForm.controls[ControlNames.PHONE].value;
    dto[this.addressType].firstName = this.addressForm.controls[ControlNames.FIRSTNAME].value;
    dto[this.addressType].secondName = this.addressForm.controls[ControlNames.SECONDNAME].value;
    dto[this.addressType].taxID = this.addressForm.controls[ControlNames.TAX_ID].value;
    dto[this.addressType].personal = this.addressForm.controls[ControlNames.PERSONAL].value ? this.addressForm.controls[ControlNames.PERSONAL].value : this.address.personal;
    dto[this.addressType].company = this.addressForm.controls[ControlNames.COMPANY].value;

    if (this.runFromMenu)
    {
      this.cs.update(dto).subscribe(
        (success) =>
        {
          this.closeModal();
        },
        (error) =>
        {
          this.showError();
        }
      );
    }
    else
    {
      this.cartService.updateCart(dto).subscribe(
        (success) =>
        {
          this.closeModal();
        },
        (error) =>
        {
          this.showError();
        }
      );
    }

    this.lastAccountType = this.address.personal ? this.AccountType.PERSONAL : this.AccountType.COMPANY;
  }

  public toggleAccountType(value: string): void
  {
    if (value === "company")
    {
      this.addressForm.controls[ControlNames.PERSONAL].setValue(false);
      this.addressForm.controls[ControlNames.COMPANY].enable();
      this.addressForm.controls[ControlNames.COMPANY].setValidators(
        Validators.required
      );
      if (this.addressType === this.addressTypeInterface.BILLING)
      {
        this.addressForm.controls[ControlNames.TAX_ID].enable();
        this.addressForm.controls[ControlNames.TAX_ID].setValidators(
          Validators.required
        );
      }

      this.addressForm.controls[ControlNames.FIRSTNAME].disable();
      this.addressForm.controls[ControlNames.SECONDNAME].disable();
    } else
    {
      this.addressForm.controls[ControlNames.PERSONAL].setValue(true);

      this.addressForm.controls[ControlNames.FIRSTNAME].enable();
      this.addressForm.controls[ControlNames.SECONDNAME].enable();
      this.addressForm.controls[ControlNames.FIRSTNAME].setValidators(
        Validators.required
      );
      this.addressForm.controls[ControlNames.SECONDNAME].setValidators(
        Validators.required
      );
      this.addressForm.controls[ControlNames.COMPANY].disable();
      this.addressForm.controls[ControlNames.TAX_ID].disable();
    }
    this.address.personal = value == "personal";

    if (
      this.addressType == AddressType.BILLING &&
      this.address.personal == false
    )
    {
      this.addressForm.controls[ControlNames.TAX_ID].setValidators(
        Validators.required
      );
    }
    this.changeDet.detectChanges();
  }

  public closeModal(event?: any, disableReload?: boolean): void
  {
    this.popupService.closePopup(this.name, !!disableReload);
    this.toggleAccountType(this.lastAccountType);

    if (this.windowRef.getNativeWindowInnerWidth() < 2040)
    {
      this.windowRef.unBlockScroll();
    }

    this.showForm = false;
    this.loaded["loaded"] = false;

    this.changeDet.detectChanges();
    this.refreshDataOnSuccess.emit();
  }

  private initializeForm(): void
  {
    if (this.addressType == AddressType.BILLING)
    {
      this.addressForm = this.formBuilder.group({
        FirstName: [
          this.address ? this.address.firstName : "",
          Validators.maxLength(127),
        ],
        SecondName: [
          this.address ? this.address.secondName : "",
          Validators.maxLength(127),
        ],
        Email: [
          this.address ? this.address.email : "",
          Validators.maxLength(127),
        ],
        Phone: [
          this.address ? this.address.phone : "",
          Validators.maxLength(127),
        ],
        Street1: [
          this.address ? this.address.street1 : "",
          [Validators.maxLength(127), Validators.required],
        ],
        PostCode: [
          this.address ? this.address.postCode : "",
          [Validators.maxLength(127), Validators.required],
        ],
        City: [
          this.address ? this.address.city : "",
          [Validators.maxLength(127), Validators.required],
        ],
        Company: [
          this.address ? this.address.company : "",
          [Validators.maxLength(127), Validators.required],
        ],
        Personal: [
          this.address.personal == undefined ? true : this.address.personal,
          Validators.maxLength(127),
        ],
        TaxID: [
          this.address ? this.address.taxID : "",
          [Validators.required, Validators.maxLength(127)]
        ],
      });
      this.address.personal = this.addressForm.controls["Personal"].value;
    }
    if (this.addressType == AddressType.CONTACT)
    {
      this.addressForm = this.formBuilder.group({
        FirstName: [
          this.address ? this.address.firstName : "",
          [Validators.maxLength(127), Validators.required],
        ],
        SecondName: [
          this.address ? this.address.secondName : "",
          [Validators.maxLength(127), Validators.required],
        ],
        Email: [
          this.address ? this.address.email : "",
          [Validators.required, CustomValidators.email],
        ],
        Phone: [
          this.address ? this.address.phone : "",
          [Validators.maxLength(127), Validators.required],
        ],
        City: [
          this.address ? this.address.city : "",
          Validators.maxLength(127),
        ],
        PostCode: [
          this.address ? this.address.postCode : "",
          Validators.maxLength(127),
        ],
        Street1: [
          this.address ? this.address.street1 : "",
          Validators.maxLength(127),
        ],
        Personal: [
          this.address.personal == undefined ? true : this.address.personal,
          Validators.maxLength(127),
        ],
        Company: [
          this.address ? this.address.company : "",
          Validators.maxLength(127),
        ],
        TaxID: [
          this.address ? this.address.taxID : "",
          Validators.maxLength(127),
        ],
      });
    }
    if (this.addressType == AddressType.DELIVERY)
    {
      this.addressForm = this.formBuilder.group({
        FirstName: [
          this.address ? this.address.firstName : "",
          Validators.maxLength(127)
        ],
        SecondName: [
          this.address ? this.address.secondName : "",
          Validators.maxLength(127)
        ],
        Phone: [
          this.address ? this.address.phone : "",
          [Validators.maxLength(127), Validators.required],
        ],
        Email: [
          this.address ? this.address.email : "",
          Validators.maxLength(127),
        ],
        Street1: [
          this.address ? this.address.street1 : "",
          [Validators.maxLength(127), Validators.required],
        ],
        PostCode: [
          this.address ? this.address.postCode : "",
          [Validators.maxLength(127), Validators.required],
        ],
        City: [
          this.address ? this.address.city : "",
          [Validators.maxLength(127), Validators.required],
        ],
        Company: [
          this.address ? this.address.company : "",
          Validators.maxLength(127),
        ],
        Personal: [
          this.address.personal == undefined ? true : this.address.personal,
          Validators.maxLength(127),
        ],
        TaxID: [
          this.address ? this.address.taxID : "",
          Validators.maxLength(127),
        ],
      });
      this.address.personal = this.addressForm.controls["Personal"].value;
    }

    this.cs.customer$
      .pipe(
        filter((v) => !!v),
        takeUntil(this.destroy$)
      )
      .subscribe((customer) =>
      {
        if (customer.isRegistered == true)
        {
          this.addressForm.controls[ControlNames.EMAIL].disable();
        }
      });
    this.addressForm.valueChanges.subscribe(() =>
    {
      this.changeDet.detectChanges();
      this.checkAndRemoveButtonErrorState();
    });
  }

  public getProperHeader(): string
  {
    const popupMode: 'add' | 'edit' = this.address === null ? 'add' : 'edit';
    if (this.addressType == AddressType.BILLING)
    {
      return this.translate.instant(`checkout.${popupMode}BillingData`);
    }
    if (this.addressType == AddressType.DELIVERY)
    {
      return this.translate.instant(`checkout.${popupMode}DeliveryData`);
    }
    if (this.addressType == AddressType.CONTACT)
    {
      return this.translate.instant(`checkout.${popupMode}ContactData`);
    }
    return '';
  }

  @HostListener("window:popstate", ["$event"])
  private backButtonHandler($event): void
  {
    if (
      !this.popupService.isActiveParam("m", this.name) &&
      this.popupService.isActivePopup(this.name)
    )
    {
      this.closeModal(null, true);
    }
  }

  private markAsTouchedAndDirty(formControls: object): void
  {
    Object.keys(formControls).forEach((el) =>
    {
      formControls[el].markAsDirty();
      formControls[el].markAsTouched();
      formControls[el].updateValueAndValidity();
    });
  }

  private showError(): void { }

  private translateFields(): void
  {
    this.secondNamePlaceholder = this.translate.instant("cart.addressField.lastName");
    this.firstNamePlaceholder = this.translate.instant("cart.addressField.firstName");
    this.addressPlaceholder = this.translate.instant("cart.addressField.address");
    this.phonePlaceholder = this.translate.instant("cart.addressField.phone");
    this.emailPlaceholder = this.translate.instant("cart.addressField.email");
    this.taxIdPlaceholder = this.translate.instant("cart.addressField.taxId");
    this.companyNamePlaceholder = this.translate.instant("cart.addressField.companyName");
    this.postCodePlaceholder = this.translate.instant("cart.addressField.postCode");
    this.cityPlaceholder = this.translate.instant("cart.addressField.city");
  }

  private checkAndRemoveButtonErrorState()
  {
    if (this.nextButton && this.addressForm.valid)
    {
      removeAnimateButton("error", this.nextButton, this.renderer2);
    }
  }

  ngOnInit(): void
  {
    this.popupService.reserveName(this.name);
  }
}
